import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const VisitsPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueVisitorsCount, setUniqueVisitorsCount] = useState(0);
  const [timeRange, setTimeRange] = useState('7d'); // 24h, 7d, 30d, 90d
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [communesPolygons, setCommunesPolygons] = useState([]);
  const [visitsCount, setVisitsCount] = useState({});
  const [loadingMap, setLoadingMap] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [tileProvider, setTileProvider] = useState('osm');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Récupérer le fond de carte configuré par l'administrateur
  useEffect(() => {
    const fetchTileParameter = async () => {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'tile')
        .single();

      if (!error && data?.value) {
        setTileProvider(String(data.value).trim().toLowerCase());
      }
    };

    fetchTileParameter();
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchChartData();
    fetchMapData();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      // Compter le total des visites
      const { count, error: countError } = await supabase
        .from('visits')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;
      setTotalCount(count || 0);

      // Compter les visiteurs uniques
      const { data: uniqueData, error: uniqueError } = await supabase
        .from('visits')
        .select('visitor_id');

      if (!uniqueError && uniqueData) {
        const uniqueVisitors = new Set(uniqueData.map(v => v.visitor_id).filter(Boolean));
        setUniqueVisitorsCount(uniqueVisitors.size);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const fetchMapData = async () => {
    setLoadingMap(true);
    try {
      const now = new Date();
      let startDate;
      
      switch (timeRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }

      // Récupérer toutes les visites avec event_id avec pagination
      let allVisitsData = [];
      let hasMore = true;
      let from = 0;
      const batchSize = 1000;

      while (hasMore) {
        const { data: visitsData, error: visitsError } = await supabase
          .from('visits')
          .select('event_id')
          .gte('visited_at', startDate.toISOString())
          .lte('visited_at', now.toISOString())
          .not('event_id', 'is', null)
          .range(from, from + batchSize - 1);

        if (visitsError) throw visitsError;

        if (visitsData && visitsData.length > 0) {
          allVisitsData = [...allVisitsData, ...visitsData];
          from += batchSize;
          hasMore = visitsData.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      // Extraire les event_id uniques
      const eventIds = [...new Set(allVisitsData.map(v => v.event_id))];

      if (eventIds.length === 0) {
        setCommunesPolygons([]);
        setVisitsCount({});
        setLoadingMap(false);
        return;
      }

      // Récupérer les codes postaux des événements
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, postal_code')
        .in('id', eventIds);

      if (eventsError) throw eventsError;

      // Créer un map event_id -> postal_code
      const eventPostalMap = {};
      eventsData.forEach(event => {
        eventPostalMap[event.id] = event.postal_code;
      });

      // Compter les visites par code postal
      const postalCounts = {};
      
      allVisitsData.forEach(visit => {
        const postalCode = eventPostalMap[visit.event_id];
        if (postalCode) {
          postalCounts[postalCode] = (postalCounts[postalCode] || 0) + 1;
        }
      });

      console.log('[Visits] Nombre de visites par code postal:', postalCounts);
      setVisitsCount(postalCounts);

      const postalCodes = Object.keys(postalCounts);
      console.log('[Visits] Codes postaux à récupérer:', postalCodes.length);

      // Récupérer les noms des communes
      const { data: communesData, error: communesError } = await supabase
        .from('communes')
        .select('code_postal, nom_de_la_commune')
        .in('code_postal', postalCodes);

      if (communesError) {
        console.error('[Visits] Erreur récupération communes:', communesError);
        return;
      }

      // Créer un mapping code_postal -> noms de communes
      const postalCodeToNames = communesData?.reduce((acc, commune) => {
        if (!acc[commune.code_postal]) {
          acc[commune.code_postal] = [];
        }
        if (commune.nom_de_la_commune && !acc[commune.code_postal].includes(commune.nom_de_la_commune)) {
          acc[commune.code_postal].push(commune.nom_de_la_commune);
        }
        return acc;
      }, {}) || {};

      // Récupérer les polygones correspondants
      const { data: polygonsData, error: polygonsError } = await supabase
        .from('communes')
        .select('code_postal, geom')
        .in('code_postal', postalCodes)
        .not('geom', 'is', null);

      if (polygonsError) {
        console.error('[Visits] Erreur récupération polygones:', polygonsError);
        return;
      }

      // Enrichir les polygones avec les noms des communes
      const enrichedPolygons = polygonsData?.map(polygon => ({
        ...polygon,
        nom: postalCodeToNames[polygon.code_postal]?.join(', ') || t('common.unknown')
      })) || [];

      console.log('[Visits] Polygones enrichis:', enrichedPolygons?.length);
      setCommunesPolygons(enrichedPolygons);
    } catch (error) {
      console.error('Erreur lors du chargement des données de la carte:', error);
    } finally {
      setLoadingMap(false);
    }
  };

  // Fonction pour obtenir la couleur selon le nombre de visites
  const getColorByVisitCount = (count) => {
    if (!count || count === 0) return '#94a3b8'; // gris si pas de visites
    
    // Dégradé du gris vers le bleu rgb(40, 148, 216) avec progression visible
    const maxCount = 50; // Ajuster selon les besoins
    const ratio = Math.min(count / maxCount, 1);
    
    // Couleur de départ (gris clair) : rgb(148, 163, 184)
    const startR = 148;
    const startG = 163;
    const startB = 184;
    
    // Couleur d'arrivée (bleu vif) : rgb(40, 148, 216)
    const endR = 40;
    const endG = 148;
    const endB = 216;
    
    // Interpolation linéaire avec courbe exponentielle
    const adjustedRatio = Math.pow(ratio, 0.7);
    const r = Math.round(startR + (endR - startR) * adjustedRatio);
    const g = Math.round(startG + (endG - startG) * adjustedRatio);
    const b = Math.round(startB + (endB - startB) * adjustedRatio);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const fetchChartData = async () => {
    setLoadingChart(true);
    try {
      const now = new Date();
      let startDate;
      let groupFormat = 'hour';
      
      switch (timeRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          groupFormat = 'hour';
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          groupFormat = 'week';
          break;
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
      }

      // Fonction helper pour obtenir le début de semaine (lundi)
      const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajuster au lundi
        d.setDate(diff);
        d.setHours(0, 0, 0, 0);
        return d;
      };

      // Récupérer toutes les données avec pagination si nécessaire
      let allData = [];
      let hasMore = true;
      let from = 0;
      const batchSize = 1000;

      while (hasMore) {
        const { data, error } = await supabase
          .from('visits')
          .select('visited_at, visitor_id')
          .gte('visited_at', startDate.toISOString())
          .lte('visited_at', now.toISOString())
          .order('visited_at', { ascending: true })
          .range(from, from + batchSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allData = [...allData, ...data];
          from += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      // Initialiser toutes les dates de la plage avec des timestamps
      const grouped = new Map();
      
      // Créer une copie pour itération
      const iterDate = new Date(startDate);
      
      // Réinitialiser l'heure de début
      if (groupFormat === 'week') {
        const weekStart = getWeekStart(iterDate);
        iterDate.setTime(weekStart.getTime());
      } else if (groupFormat === 'day') {
        iterDate.setHours(0, 0, 0, 0);
      } else {
        iterDate.setMinutes(0, 0, 0);
      }
      
      const endDate = new Date(now);
      if (groupFormat === 'day') {
        endDate.setHours(23, 59, 59, 999);
      } else if (groupFormat === 'week') {
        endDate.setHours(23, 59, 59, 999);
      }
      
      while (iterDate <= endDate) {
        const timestamp = iterDate.getTime();
        let displayKey;
        
        if (groupFormat === 'hour') {
          displayKey = `${String(iterDate.getDate()).padStart(2, '0')}/${String(iterDate.getMonth() + 1).padStart(2, '0')} ${String(iterDate.getHours()).padStart(2, '0')}h`;
        } else if (groupFormat === 'week') {
          const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
          displayKey = `${String(iterDate.getDate()).padStart(2, '0')} ${months[iterDate.getMonth()]}`;
        } else {
          displayKey = `${String(iterDate.getDate()).padStart(2, '0')}/${String(iterDate.getMonth() + 1).padStart(2, '0')}`;
        }
        
        grouped.set(timestamp, {
          timestamp,
          displayKey,
          total: 0,
          uniqueVisitors: new Set()
        });

        if (groupFormat === 'hour') {
          iterDate.setTime(iterDate.getTime() + 60 * 60 * 1000);
        } else if (groupFormat === 'week') {
          iterDate.setTime(iterDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        } else {
          iterDate.setTime(iterDate.getTime() + 24 * 60 * 60 * 1000);
        }
      }

      // Remplir avec les données réelles
      allData.forEach(visit => {
        const date = new Date(visit.visited_at);
        let timestamp;
        
        if (groupFormat === 'hour') {
          const hourDate = new Date(date);
          hourDate.setMinutes(0, 0, 0);
          timestamp = hourDate.getTime();
        } else if (groupFormat === 'week') {
          const weekStart = getWeekStart(date);
          timestamp = weekStart.getTime();
        } else {
          const dayDate = new Date(date);
          dayDate.setHours(0, 0, 0, 0);
          timestamp = dayDate.getTime();
        }

        // Trouver le bucket correspondant (exact ou le plus proche avant)
        let bucket = grouped.get(timestamp);
        
        // Si pas de match exact, chercher le bucket de semaine qui contient cette date
        if (!bucket && groupFormat === 'week') {
          for (const [bucketTimestamp, bucketData] of grouped.entries()) {
            const bucketEnd = bucketTimestamp + (7 * 24 * 60 * 60 * 1000);
            if (timestamp >= bucketTimestamp && timestamp < bucketEnd) {
              bucket = bucketData;
              break;
            }
          }
        }

        if (bucket) {
          bucket.total++;
          if (visit.visitor_id) {
            bucket.uniqueVisitors.add(visit.visitor_id);
          }
        }
      });

      // Convertir en tableau trié par timestamp
      const chartArray = Array.from(grouped.values())
        .sort((a, b) => a.timestamp - b.timestamp)
        .map(item => ({
          date: item.displayKey,
          [t('settings.admin.visits.chart.totalVisits')]: item.total,
          [t('settings.admin.visits.chart.uniqueVisits')]: item.uniqueVisitors.size
        }));

      setChartData(chartArray);
    } catch (error) {
      console.error('Erreur lors du chargement des données du graphique:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  // Initialiser et mettre à jour la carte
  useEffect(() => {
    if (!mapRef.current) return;

    // Initialiser la carte si nécessaire
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([46.603354, 1.888334], 6);

      const baseLayers = {
        osm: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }),
        stadiaSmooth: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaSmoothDark: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stradiamaps: L.tileLayer(`https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaOSMBright: L.tileLayer(`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        satellite: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: '&copy; <a href="https://www.esri.com">Esri</a>'
        }),
        terrain: L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        })
      };

      const layerNames = {
        osm: t('map.standard'),
        stadiaSmooth: t('map.stadiaSmooth'),
        stadiaSmoothDark: t('map.stadiaSmoothDark'),
        stradiamaps: t('map.stadiaOutdoors'),
        stadiaOSMBright: t('map.stadiaOSMBright'),
        satellite: t('map.satellite'),
        terrain: t('map.terrain')
      };
      const selectedProvider = baseLayers[tileProvider] ? tileProvider : 'osm';
      baseLayers[selectedProvider].addTo(mapInstanceRef.current);
      L.control.layers(
        Object.fromEntries(Object.entries(baseLayers).map(([key, layer]) => [layerNames[key], layer])),
        undefined,
        { position: 'topright' }
      ).addTo(mapInstanceRef.current);
    }

    // Nettoyer les couches existantes
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.Marker) {
        mapInstanceRef.current.removeLayer(layer);
      }
    });

    const bounds = L.latLngBounds([]);

    // Afficher les communes avec visites
    if (communesPolygons.length > 0) {
      communesPolygons.forEach(commune => {
        try {
          const geojson = typeof commune.geom === 'string' 
            ? JSON.parse(commune.geom) 
            : commune.geom;

          const communeName = commune.nom || t('common.unknown');
          const postalCode = commune.code_postal || '';
          const visitCount = visitsCount[postalCode] || 0;
          const color = getColorByVisitCount(visitCount);
          
          const geoJsonLayer = L.geoJSON(geojson, {
            style: {
              color: color,
              weight: 2,
              opacity: 1,
              fillOpacity: 0.4,
              fillColor: color,
              smoothFactor: 2.0
            }
          }).bindPopup(`<b>${communeName}</b>${postalCode ? `<br>${postalCode}` : ''}<br>${visitCount} ${visitCount > 1 ? 'visites' : 'visite'}`);

          geoJsonLayer.addTo(mapInstanceRef.current);
          bounds.extend(geoJsonLayer.getBounds());

          // Marqueur avec le dégradé bleu
          if (showMarkers) {
            const center = geoJsonLayer.getBounds().getCenter();
            
            const divIcon = L.divIcon({
              className: 'visit-count-marker',
              html: `<div style="
                background: ${color};
                color: white;
                border: 3px solid white;
                border-radius: 50%;
                width: ${Math.min(70, 35 + Math.sqrt(visitCount) * 4)}px;
                height: ${Math.min(70, 35 + Math.sqrt(visitCount) * 4)}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: ${visitCount > 99 ? '11px' : '15px'};
                box-shadow: 0 4px 8px rgba(0,0,0,0.4);
                transition: all 0.3s ease;
              ">${visitCount}</div>`,
              iconSize: [Math.min(70, 35 + Math.sqrt(visitCount) * 4), Math.min(70, 35 + Math.sqrt(visitCount) * 4)],
              iconAnchor: [Math.min(70, 35 + Math.sqrt(visitCount) * 4) / 2, Math.min(70, 35 + Math.sqrt(visitCount) * 4) / 2]
            });

            const marker = L.marker(center, { icon: divIcon });
            marker.bindTooltip(`${communeName}<br>${postalCode}<br>${visitCount} ${visitCount > 1 ? 'visites' : 'visite'}`, {
              permanent: false,
              direction: 'top'
            });
            marker.addTo(mapInstanceRef.current);
          }
        } catch (error) {
          console.error('[Visits] Erreur parsing géométrie:', error, commune);
        }
      });

      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [communesPolygons, visitsCount, showMarkers, tileProvider, t, timeRange]);

  if (adminLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <style>{`
        .visits-map .leaflet-top,
        .visits-map .leaflet-bottom {
          z-index: 1000;
        }

        .visits-map .leaflet-control-layers-toggle {
          background-color: white;
          color: #2894D8;
        }

        .visits-map .leaflet-control-layers-toggle:hover {
          background-color: #2894D8;
        }

        .visits-map .leaflet-control-layers {
          border: 0;
        }
      `}</style>
      <PageHeader title={t('settings.admin.visits.title')} backPath="/settings" />

      <div className="p-4">
        {/* Graphique */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <h2 className="text-h2 mb-4">{t('settings.admin.visits.chart.title')}</h2>
          
          {/* Filtres temporels */}
          <div className="flex gap-2 overflow-x-auto mb-4">
            <button
              onClick={() => setTimeRange('24h')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '24h'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.24h')}
            </button>
            <button
              onClick={() => setTimeRange('7d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '7d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.7d')}
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '30d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.30d')}
            </button>
            <button
              onClick={() => setTimeRange('90d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '90d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.90d')}
            </button>
          </div>

          {/* Graphique */}
          {loadingChart ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2894D8]"></div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">{t('settings.admin.visits.chart.noData')}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={t('settings.admin.visits.chart.totalVisits')}
                  stroke="#2894D8" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line 
                  type="monotone" 
                  dataKey={t('settings.admin.visits.chart.uniqueVisits')}
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Carte des communes */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-h2">{t('settings.admin.visits.map.title')}</h2>
            <button
              onClick={() => setShowMarkers(!showMarkers)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                showMarkers 
                  ? 'bg-[#2894D8] text-white hover:bg-[#136E9D]' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {showMarkers ? 'Masquer marqueurs' : 'Afficher marqueurs'}
            </button>
          </div>
          
          {loadingMap ? (
            <div className="flex items-center justify-center h-[600px]">
              <LoadingSpinner />
            </div>
          ) : communesPolygons.length === 0 ? (
            <div className="h-[600px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500">{t('settings.admin.visits.map.noData')}</p>
            </div>
          ) : (
            <div
              ref={mapRef} 
              className="visits-map h-[600px] rounded-lg border border-gray-200"
              style={{ zIndex: 1 }}
            />
          )}
          {communesPolygons.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {communesPolygons.length} {communesPolygons.length > 1 ? 'communes' : 'commune'} avec visites
            </p>
          )}
        </div>

        {/* Statistiques */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('settings.admin.visits.chart.totalVisits')}</p>
              <p className="text-2xl font-bold text-[#2894D8]">{totalCount}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('settings.admin.visits.chart.uniqueVisits')}</p>
              <p className="text-2xl font-bold text-[#10B981]">{uniqueVisitorsCount}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitsPage;
